import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import AvatarImage from "../../components/AvatarImage";
import CameraCaptureModal from "../../components/CameraCaptureModal";
import { Camera, Eye, EyeOff } from "lucide-react";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { addProfileData } from "../../../../redux/slices/profileslice";
import {
  ADDRESS_MAX_LENGTH,
  NAME_MAX_LENGTH,
  SWAL_SETTINGS,
  ROLE
} from "../../../../utils/Constants";
import { useNavigate } from "react-router-dom";
import { handleServerValidations, getUser, setLocalKey, isUserMobileMissing } from "../../../../utils/commonfunction";
import { editProfile, profileDetails, sendUpdateOtp, verifyUpdateOtp } from "../../services/user.service";
// import { PHONE_VALIDATION } from "../../../../utils/CommonValidatiobn";
import PhoneInput from "../../../../utils/PhoneInput";
import { useTranslation } from "react-i18next";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import Select from "react-select";
import { getProductCategories } from "../../services/stream.service";

function ProfileEdit() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [submitted, setSubmitted] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const userData = getUser(ROLE.USER) || {};
  const [coords, setCoords] = useState({
    latitude: userData?.latitude || "",
    longitude: userData?.longitude || "",
  });
  const [isDetailsChanged, setIsDetailChanged] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);


  const getPhoneNumberPart = (value) => {
    if (!value || typeof value !== "string") return "";
    const parts = value.trim().split(" ");
    if (parts.length > 1) {
      return parts.slice(1).join("").trim();
    }
    return value.replace(/^\+\d{1,4}/, "").trim() || value.replace(/\D/g, "");
  };

  const PHONE_VALIDATION = Yup.string()
    .test("phone_required", "Phone number is required", (value) => {
      const number = getPhoneNumberPart(value);
      return number.length > 0;
    })
    .test("phone_digits", "Phone number must contain digits only", (value) => {
      const number = getPhoneNumberPart(value);
      return /^\d+$/.test(number);
    })
    .test("phone_min_length", "Phone number is too short", (value) => {
      const number = getPhoneNumberPart(value);
      return number.length >= 7;
    })
    .test("phone_max_length", "Phone number is too long", (value) => {
      const number = getPhoneNumberPart(value);
      return number.length <= 15;
    });

  const loginByEmail = !!userData?.email; // Logged in with email / Google

  // Validation only for visible inputs
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .max(NAME_MAX_LENGTH, t("Name_Validation_maximum_length")),

    username: Yup.string()
      .required("Username is required")
      .matches(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores")
      .min(3, "Username must be at least 3 characters")
      .max(30, "Username must be at most 30 characters"),

    email: loginByEmail
      ? Yup.string().trim().email("Invalid email").required("Email is required")
      : Yup.string().nullable(),

    address: Yup.string().max(
      ADDRESS_MAX_LENGTH,
      t("Address_Validation_maximum_length"),
    ),

    phone_number: PHONE_VALIDATION,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userData?.name || "",
      username: userData?.username || "",
      email: userData?.email || "",
      address: userData?.address || "",
      phone_number: userData?.phone_number || "",
      profile_image: "",
      is_profile_public: userData?.is_profile_public !== undefined ? userData.is_profile_public : true,
      preferred_categories: (userData?.preferred_categories || []).map(String),
    },
    validationSchema,
    onSubmit: (values) => {
      setSubmitted(true);

      let formData = new FormData();
      formData.append("_id", userData._id);
      formData.append("name", values.name);
      formData.append("username", values.username);
      formData.append("email", values.email);
      formData.append("address", values.address);
      formData.append("latitude", coords.latitude);
      formData.append("longitude", coords.longitude);
      formData.append("phone_number", values.phone_number);
      formData.append("profile_image", values.profile_image);
      formData.append("is_profile_public", values.is_profile_public);
      formData.append("preferred_categories", JSON.stringify(values.preferred_categories));

      editProfile(formData)
        .then((response) => {
          if (response.success) {
            let oldUser = getUser(ROLE.USER) || {};
            let updatedUser = {
              ...oldUser,
              name: values.name,
              username: values.username,
              email: values.email,
              address: values.address,
              phone_number: values.phone_number,
              is_profile_public: values.is_profile_public,
              preferred_categories: values.preferred_categories,
            };
            setLocalKey(ROLE.USER, JSON.stringify(updatedUser));
            dispatch(addProfileData(updatedUser));

            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            setIsDetailChanged((prev) => !prev);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => console.log(error))
        .finally(() => {
          setTimeout(() => {
            setSubmitted(false);
          }, 1000);
        });
    },
  });

  const handleSendOtp = async (phoneNumber) => {
    setIsVerifyingOtp(true);
    try {
      const cleanPhone = phoneNumber.replace(/\D/g, "");

      const payload = {
        _id: userData._id,
        phone_number: cleanPhone,
      };
      const response = await sendUpdateOtp(payload);

      if (response.success) {
        setOtpSent(true);
        // Show SweetAlert2 Modal for OTP input
        const { value: otp } = await Swal.fire({
          title: "Verify Phone Number",
          input: "text",
          inputLabel: "Enter 6-digit OTP",
          inputPlaceholder: "",
          showCancelButton: true,
          confirmButtonText: "Verify",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#f43f5e", // Using primary-500 color approx
          inputAttributes: {
            maxlength: 6,
            autocapitalize: "off",
            autocorrect: "off",
          },
          inputValidator: (value) => {
            if (!value || value.length !== 6 || !/^\d+$/.test(value)) {
              return "Please enter a valid 6-digit OTP";
            }
          },
        });

        if (otp) {
          handleVerifyOtp(phoneNumber, otp);
        } else {
          setOtpSent(false); // Cancelled
        }
      } else {
        Swal.fire({
          icon: "error",
          text: handleServerValidations(response) || "Failed to send OTP",
          ...SWAL_SETTINGS,
        });
        setOtpSent(false);
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        text: "Something went wrong while sending OTP.",
        ...SWAL_SETTINGS,
      });
      setOtpSent(false);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleVerifyOtp = async (phoneNumber, otp) => {
    try {
      const payload = {
        _id: userData._id,
        phoneOtp: otp,
        type: "phone",
      };
      const response = await verifyUpdateOtp(payload);

      if (response.success) {
        Swal.fire({
          icon: "success",
          text: "Phone number verified successfully!",
          ...SWAL_SETTINGS,
        });
        setIsDetailChanged((prev) => !prev); // Trigger refetch to update UI badge
        setOtpSent(false);
      } else {
        Swal.fire({
          icon: "error",
          text: handleServerValidations(response) || "Invalid OTP",
          ...SWAL_SETTINGS,
        });
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        text: "Something went wrong during verification.",
        ...SWAL_SETTINGS,
      });
    }
  };

  const handleOpenProfileCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      Swal.fire({
        icon: "warning",
        text: "Please connect camera first",
        ...SWAL_SETTINGS,
      });
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((device) => device.kind === "videoinput");
      if (devices.length > 0 && videoInputs.length === 0) {
        Swal.fire({
          icon: "warning",
          text: "Please connect camera first",
          ...SWAL_SETTINGS,
        });
        return;
      }
      setIsCameraModalOpen(true);
    } catch (err) {
      console.error("Camera check error:", err);
      Swal.fire({
        icon: "warning",
        text: "Please connect camera first",
        ...SWAL_SETTINGS,
      });
    }
  };

  const handlePhotoCaptured = (file, previewUrl) => {
    formik.setFieldValue("profile_image", file);
    setPreviewImage(previewUrl);
  };

  const handleToggleVisibility = async () => {
    const nextVal = !formik.values.is_profile_public;
    formik.setFieldValue("is_profile_public", nextVal);

    try {
      let formData = new FormData();
      formData.append("_id", userData._id);
      formData.append("is_profile_public", nextVal);
      const response = await editProfile(formData);
      if (response.success) {
        let oldUser = getUser(ROLE.USER) || {};
        let updatedUser = {
          ...oldUser,
          is_profile_public: nextVal,
        };
        setLocalKey(ROLE.USER, JSON.stringify(updatedUser));
        dispatch(addProfileData(updatedUser));

        Swal.fire({
          icon: "success",
          text: nextVal
            ? "Profile is now Public (visible to other users)"
            : "Profile is now Hidden (other users will only see your first letter)",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await profileDetails({ _id: userData._id });

        let oldUser = getUser(ROLE.USER) || {};

        let newUser = {
          ...oldUser,
          name: response.data?.name,
          username: response.data?.username,
          email: response.data?.email,
          address: response.data?.address,
          phone_number: response.data?.phone_number,
          profile_image: response.data?.profile_image,
          is_profile_public: response.data?.is_profile_public !== undefined ? response.data?.is_profile_public : true,
          preferred_categories: response.data?.preferred_categories || [],
          isAdvertiser: response.data?.isAdvertiser,
          isKycRegistered: response.data?.isKycRegistered,
          isBusinessRegistered: response.data?.isBusinessRegistered,
          is_kyc_verified: response.data?.is_kyc_verified,
          is_phone_verified: response.data?.is_phone_verified,
        };

        setLocalKey(ROLE.USER, JSON.stringify(newUser));
        dispatch(addProfileData(newUser));
      } catch (err) {
        console.log(err);
      }
    };

    fetchProfile();
  }, [isDetailsChanged]);

  useEffect(() => {
    setCategoriesLoading(true);
    getProductCategories()
      .then((resp) => {
        if (resp?.success) {
          setCategoryOptions(
            (resp.data?.list || []).map((cat) => ({ value: String(cat._id), label: cat.title })),
          );
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setCategoriesLoading(false));
  }, []);

  useEffect(() => {
    if (isUserMobileMissing(userData) && !Swal.isVisible()) {
      Swal.fire({
        icon: "warning",
        title: "Mobile Number Required",
        text: "Please add your mobile number to continue.",
        confirmButtonColor: "var(--primary-400, #ff4081)",
        confirmButtonText: "OK",
      });
    }
  }, []);

  return (
    <div className="edit-profile-bg py-10">
      <div className="container">
        <div className="max-w-[797px] mx-auto">
          <h1 className="headings-h1 text-zinc-950 border-b pb-[15px] mb-[30px]">
            {t("edit_profile")}
          </h1>
          {isUserMobileMissing(userData) && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-2xl flex items-center gap-3 text-amber-900 shadow-sm">
              <svg className="w-6 h-6 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-bold text-sm">Mobile Number Required</p>
                <p className="text-xs text-amber-700">Please add your mobile number below and click Update to continue using the application.</p>
              </div>
            </div>
          )}
          <form onSubmit={formik.handleSubmit}>
            <div className="bg-white p-[30px] rounded-[10px] shadow">
              {/* Profile Photo Section */}
              {/* <div className="bg-primary-50 p-[15px] rounded-[10px] flex items-center justify-between">
                <div className="flex items-center gap-[10px]">
                  <img src={previewImage || userData?.profile_image || "/images/User.png"} className="w-[89px] h-[89px] object-cover rounded-full" />
                  <h2 className="headings-h2 text-zinc-950">{formik.values.name || "User"}</h2>
                </div>

                <label className="btn btn-pri cursor-pointer">
                  {t("upload_photo")}
                  <input type="file" accept="image/png, image/jpeg, image/jpg" className="hidden" onChange={handleImageChange} />
                </label>
              </div> */}

              <div className="bg-primary-50 p-[15px] rounded-[10px] flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-[10px] min-w-0">
                  <div
                    className="relative cursor-pointer group flex-shrink-0"
                    onClick={handleOpenProfileCamera}
                    title={t("take_photo") || "Take Photo"}
                  >
                    <AvatarImage
                      src={previewImage || userData?.profile_image}
                      name={formik.values.name || userData?.name}
                      alt={formik.values.name || "User profile"}
                      className="w-[89px] h-[89px] object-cover rounded-full flex-shrink-0"
                      style={{ width: "89px", height: "89px", objectFit: "cover", borderRadius: "50%", flexShrink: 0 }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenProfileCamera();
                      }}
                      className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#FF63B5] text-white flex items-center justify-center p-0 border-0 cursor-pointer shadow-md hover:bg-[#e04f9f] transition-colors focus:outline-none"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                      title={t("take_photo") || "Take Photo"}
                    >
                      <Camera className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="min-w-0">
                    <h2 className="headings-h2 text-zinc-950 truncate">
                      {formik.values.name || "User"}
                    </h2>
                    <span
                      className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-medium mt-1 ${
                        formik.values.is_profile_public
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {formik.values.is_profile_public ? (
                        <>
                          <Eye className="w-3 h-3" />
                          <span>{t("public_to_others") || "Public to others"}</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3" />
                          <span>{t("hidden_to_others") || "Hidden to others (initial only)"}</span>
                        </>
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 flex-wrap">
                  {/* Profile Visibility Toggle Switch */}
                  <div
                    onClick={handleToggleVisibility}
                    className="flex items-center gap-3 bg-white px-3.5 rounded-[6px] border border-zinc-200 hover:border-zinc-300 shadow-xs cursor-pointer select-none transition-all flex-shrink-0"
                    style={{ height: "45px" }}
                    title={
                      formik.values.is_profile_public
                        ? "Public: Click to hide profile picture from other users"
                        : "Hidden: Click to show profile picture to other users"
                    }
                  >
                    <span className="text-xs font-semibold text-zinc-700 whitespace-nowrap">
                      {formik.values.is_profile_public
                        ? (t("show_profile") || "Show Profile")
                        : (t("hide_profile") || "Hide Profile")}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formik.values.is_profile_public}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleVisibility();
                      }}
                      className="relative flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
                      style={{
                        width: "46px",
                        height: "26px",
                        backgroundColor: formik.values.is_profile_public ? "#FF63B5" : "#D4D4D8",
                        padding: "3px",
                        border: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        boxSizing: "border-box",
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#FFFFFF",
                          borderRadius: "50%",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.25)",
                          transform: formik.values.is_profile_public ? "translateX(20px)" : "translateX(0px)",
                          transition: "transform 0.2s ease-in-out",
                          display: "inline-block",
                          flexShrink: 0,
                        }}
                      />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleOpenProfileCamera}
                    className="btn btn-pri flex-shrink-0"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      whiteSpace: "nowrap",
                      height: "45px",
                      lineHeight: "1",
                      padding: "0 20px",
                    }}
                  >
                    <Camera className="w-4 h-4 flex-shrink-0" style={{ display: "inline-block", margin: 0 }} />
                    <span style={{ display: "inline-block", lineHeight: "1" }}>
                      {t("take_photo") || "Take Photo"}
                    </span>
                  </button>
                </div>
              </div>

              {/* Form Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[15px] mt-5">
                {/* Name */}
                <div className="form-group">
                  <label>{t("name")}</label>
                  <input
                    type="text"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    className="form-control-custom"
                  />
                  {formik.errors.name && (
                    <p className="error-text text-danger">
                      {formik.errors.name}
                    </p>
                  )}
                </div>

                {/* Username */}
                <div className="form-group">
                  <label>{t("username") || "Username"}</label>
                  <input
                    type="text"
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    className="form-control-custom"
                  />
                  {formik.errors.username && (
                    <p className="error-text text-danger">
                      {formik.errors.username}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="form-group">
                  <label>{t("email")}</label>
                  <input
                    type="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    disabled
                    className="form-control-custom bg-gray-100 cursor-not-allowed"
                  />
                  {formik.errors.email && (
                    <p className="error-text  text-danger">
                      {formik.errors.email}
                    </p>
                  )}
                </div>
              </div>

              <>
                  {/* Address */}
                  <div className="form-group mt-3">
                    <label>{t("address")}</label>
                    {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                      <GooglePlacesAutocomplete
                        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                        selectProps={{
                          value: formik.values.address
                            ? {
                                label: formik.values.address,
                                value: formik.values.address,
                              }
                            : null,
                          onChange: (val) => {
                            formik.setFieldValue("address", val.label);
                            if (window.google) {
                              const geocoder = new window.google.maps.Geocoder();
                              const query = val?.value?.place_id
                                ? { placeId: val.value.place_id }
                                : { address: val.label };

                              geocoder.geocode(
                                query,
                                (results, status) => {
                                  if (status === "OK" && results && results[0]) {
                                    const lat = results[0].geometry.location.lat();
                                    const lng = results[0].geometry.location.lng();
                                    setCoords({ latitude: lat, longitude: lng });
                                  }
                                },
                              );
                            }
                          },
                          placeholder: t("address"),
                          classNamePrefix: "react-select",
                          styles: {
                            control: (provided) => ({
                              ...provided,
                              backgroundColor: "#f9fafb",
                              borderColor: "#e5e7eb",
                              borderRadius: "8px",
                              padding: "2px",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#d1d5db",
                              },
                            }),
                            menu: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          },
                        }}
                      />
                    ) : (
                      <input
                        type="text"
                        name="address"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        className="form-control-custom"
                      />
                    )}
                    {formik.errors.address && (
                      <p className="error-text  text-danger">
                        {formik.errors.address}
                      </p>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div className="form-group mt-3">
                    <div className="flex justify-between items-end mb-2">
                      <label className="mb-0">{t("phone_number")}</label>
                      {userData.phone_number && userData.phone_number === formik.values.phone_number && (
                        userData.is_phone_verified ? (
                          <span className="text-green-500 font-medium text-sm flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                            Verified
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-primary-500 text-sm font-medium hover:underline focus:outline-none"
                            onClick={() => handleSendOtp(formik.values.phone_number)}
                            disabled={isVerifyingOtp}
                          >
                            {isVerifyingOtp ? "Sending..." : "Verify Now"}
                          </button>
                        )
                      )}
                    </div>
                    <PhoneInput
                      value={formik.values.phone_number}
                      onChange={(val) => {
                        // val = { code, number, phone_number }
                        formik.setFieldValue(
                          "phone_number",
                          val?.phone_number || "",
                        );
                      }}
                    />

                    {formik.errors.phone_number && (
                      <p className="error-text  text-danger">
                        {formik.errors.phone_number}
                      </p>
                    )}
                  </div>

                  {/* Preferred Categories */}
                  <div className="form-group mt-3">
                    <label>{t("preferred_categories")}</label>
                    <Select
                      isMulti
                      isLoading={categoriesLoading}
                      options={categoryOptions}
                      value={categoryOptions.filter((opt) =>
                        formik.values.preferred_categories.includes(opt.value),
                      )}
                      onChange={(selected) =>
                        formik.setFieldValue(
                          "preferred_categories",
                          (selected || []).map((opt) => opt.value),
                        )
                      }
                      placeholder={t("select_preferred_categories")}
                      closeMenuOnSelect={false}
                      classNamePrefix="react-select"
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: "#f9fafb",
                          borderColor: "#e5e7eb",
                          borderRadius: "8px",
                          padding: "2px",
                          boxShadow: "none",
                          "&:hover": {
                            borderColor: "#d1d5db",
                          },
                        }),
                        multiValue: (provided) => ({
                          ...provided,
                          backgroundColor: "#FFE4F2",
                          borderRadius: "6px",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
              </>

              <button
                className="btn btn-pri min-w-[181px] mt-5"
                type="submit"
                disabled={submitted}
              >
                {submitted ? t("updating") : t("update")}
              </button>
            </div>
          </form>
        </div>
      </div>
      <CameraCaptureModal
        isOpen={isCameraModalOpen}
        onClose={() => setIsCameraModalOpen(false)}
        onCapture={handlePhotoCaptured}
      />
    </div>
  );
}

export default ProfileEdit;
